// src/pages/super-admin/CompanyListPage.js - 기업 관리 리스트

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faSearch } from '@fortawesome/free-solid-svg-icons';
import { getCompanies } from '../../services/api';
import './EnterpriseListPage.scss';

const STATUS_LABELS = {
  active: '활성',
  inactive: '비활성',
  deleted: '삭제됨',
};

const PRESET_LABELS = {
  telecom: '통신',
  finance: '금융',
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('ko-KR');
};

function CompanyListPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, per_page: 20 };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const data = await getCompanies(params);
      setCompanies(data.companies || []);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total_count || 0);
    } catch (err) {
      console.error('회사 목록 조회 실패:', err);
      setError('회사 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCompanies();
  };

  return (
    <div className="enterprise-list-page">
      <div className="sa-page-header">
        <div className="sa-page-header-left">
          <h1 className="sa-page-title">기업 관리</h1>
          <p className="sa-page-subtitle">총 {totalCount}개의 기업</p>
        </div>
      </div>

      <div className="sa-table-container">
        <div className="sa-table-toolbar">
          <div className="sa-table-toolbar-left">
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                className="sa-search-input"
                placeholder="기업명 검색..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button type="submit" className="sa-btn sa-btn-secondary sa-btn-sm">
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </form>
            <select
              className="sa-filter-select"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">전체 상태</option>
              <option value="active">활성</option>
              <option value="inactive">비활성</option>
              <option value="deleted">삭제됨</option>
            </select>
          </div>
        </div>

        {loading && <div className="sa-loading">불러오는 중...</div>}

        {error && (
          <div className="sa-empty">
            <div className="sa-empty-icon">⚠️</div>
            <div className="sa-empty-text">{error}</div>
            <button className="sa-btn sa-btn-secondary" onClick={fetchCompanies}>다시 시도</button>
          </div>
        )}

        {!loading && !error && (
          <>
            <table className="sa-table">
              <thead>
                <tr>
                  <th>기업명</th>
                  <th>Persona 프리셋</th>
                  <th>상태</th>
                  <th>전체 사용자 수</th>
                  <th>생성일</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {companies.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="sa-empty">
                        <div className="sa-empty-icon"><FontAwesomeIcon icon={faBuilding} style={{ fontSize: 36, color: '#d1d5db' }} /></div>
                        <div className="sa-empty-text">기업이 없습니다</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  companies.map((company) => (
                    <tr key={company.id}>
                      <td>
                        <button
                          type="button"
                          className="sa-btn-link"
                          onClick={() => navigate(`/super-admin/companies/${company.id}`)}
                          style={{ background: 'none', border: 'none', color: '#111827', cursor: 'pointer', fontWeight: 700, padding: 0 }}
                        >
                          {company.name || `기업 #${company.id}`}
                        </button>
                      </td>
                      <td style={{ fontSize: 13, color: '#374151' }}>
                        {PRESET_LABELS[company.personaPresetId || company.persona_preset_id] || company.personaPresetId || company.persona_preset_id || 'telecom'}
                      </td>
                      <td>
                        <span className={`sa-badge ${company.status === 'active' ? 'sa-badge-active' : 'sa-badge-inactive'}`}>
                          {STATUS_LABELS[company.status] || company.status || '-'}
                        </span>
                      </td>
                      <td>{company.member_count ?? 0}</td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>{formatDate(company.created_at)}</td>
                      <td>
                        <button
                          className="sa-btn sa-btn-secondary sa-btn-sm enterprise-text-action"
                          onClick={() => navigate(`/super-admin/companies/${company.id}`)}
                        >
                          상세
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="sa-pagination">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
                <span className="page-info">{page} / {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>다음</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CompanyListPage;
